<script lang="ts">
  import Layout from "../../components/layout/Layout.svelte";
  import Button from "../../components/input/Button.svelte";
  import TextInput from "../../components/input/TextInput.svelte";
  import { addNotification } from "../../lib/stores";

  let {
    user = null,
    code,
    expiresAt,
    appStoreUrl = null,
    playStoreUrl = null,
    trip,
    viewer,
  }: {
    user?: { id: string; name: string; email: string; avatarUrl?: string } | null;
    code: string;
    expiresAt: string;
    appStoreUrl?: string | null;
    playStoreUrl?: string | null;
    trip: {
      name: string;
      destination: string;
      startDate: string;
      endDate: string;
      ownerName: string;
      memberCount: number;
      memberEmojis: string[];
    };
    viewer: { loggedIn: boolean; alreadyJoined: boolean };
  } = $props();

  // 프리셋 이모지 12종 — 서버 색상 팔레트와 별개로 게스트가 직접 고른다
  const EMOJI_PRESETS = [
    "😀", "😎", "🥳", "🦊", "🐱", "🐶",
    "🐻", "🐼", "🦁", "🐯", "🐨", "🦄",
  ];

  let nickname = $state("");
  let selectedEmoji = $state(EMOJI_PRESETS[0]);
  let loading = $state(false);
  let errorMessage = $state("");

  function csrfToken(): string {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  }

  function formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric" });
  }

  async function joinAsGuest() {
    if (!nickname.trim()) {
      errorMessage = "닉네임을 입력해 주세요.";
      return;
    }

    loading = true;
    errorMessage = "";
    try {
      const response = await fetch(`/api/v1/invites/${code}/guest_join`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({ nickname: nickname.trim(), emoji: selectedEmoji, web: true }),
      });
      const json = await response.json();

      if (response.ok && json.success) {
        window.location.href = `/trips/${json.data.trip_id}`;
      } else {
        errorMessage = json.error?.message || "참여에 실패했어요. 다시 시도해 주세요.";
      }
    } catch (error) {
      errorMessage = "네트워크 오류가 발생했어요.";
    } finally {
      loading = false;
    }
  }

  async function acceptWithAccount() {
    loading = true;
    errorMessage = "";
    try {
      const response = await fetch(`/api/v1/invites/${code}/accept`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
      });
      const json = await response.json();

      if (response.ok && json.success) {
        window.location.href = `/trips/${json.data.trip_id}`;
      } else {
        errorMessage = json.error?.message || "참여에 실패했어요. 다시 시도해 주세요.";
        addNotification("error", errorMessage);
      }
    } catch (error) {
      errorMessage = "네트워크 오류가 발생했어요.";
      addNotification("error", errorMessage);
    } finally {
      loading = false;
    }
  }

  async function openTrip() {
    loading = true;
    try {
      const response = await fetch(`/api/v1/invites/${code}`, {
        headers: { "X-CSRF-Token": csrfToken() },
      });
      const json = await response.json();

      if (response.ok && json.success) {
        window.location.href = `/trips/${json.data.trip.id}`;
      } else {
        addNotification("error", "여행 정보를 불러오지 못했어요.");
      }
    } catch (error) {
      addNotification("error", "네트워크 오류가 발생했어요.");
    } finally {
      loading = false;
    }
  }
</script>

<Layout {user}>
  <div class="min-h-screen bg-app flex items-center justify-center p-4">
    <div class="w-full max-w-md">
      <div class="bg-surface rounded-2xl shadow-xl overflow-hidden">
        <div class="h-32 bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center">
          <span class="text-4xl">✈️</span>
        </div>

        <div class="p-6">
          <!-- Trip Summary -->
          <div class="text-center mb-6 [word-break:keep-all] [overflow-wrap:break-word]">
            <p class="text-sm text-muted mb-1">
              <span class="font-medium text-primary">{trip.ownerName}</span>님이 초대했어요
            </p>
            <h1 class="text-2xl font-bold text-primary mb-2">
              '{trip.name}' 여행에<br />초대받았어요
            </h1>
            <p class="text-muted flex items-center justify-center gap-1">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              </svg>
              {trip.destination}
            </p>
          </div>

          <div class="bg-surface-hover rounded-xl p-4 mb-6">
            <div class="grid grid-cols-2 gap-4 text-sm mb-3">
              <div>
                <p class="text-muted">여행 기간</p>
                <p class="font-medium text-primary">
                  {formatDate(trip.startDate)} ~ {formatDate(trip.endDate)}
                </p>
              </div>
              <div>
                <p class="text-muted">참여 인원</p>
                <p class="font-medium text-primary">{trip.memberCount}명</p>
              </div>
            </div>
            {#if trip.memberEmojis.length > 0}
              <div class="flex gap-1 pt-1">
                {#each trip.memberEmojis as emoji}
                  <span class="w-8 h-8 rounded-full bg-surface flex items-center justify-center text-lg">{emoji}</span>
                {/each}
              </div>
            {/if}
          </div>

          <!-- Actions -->
          {#if viewer.alreadyJoined}
            <div class="text-center">
              <p class="text-muted mb-4 [word-break:keep-all] [overflow-wrap:break-word]">이미 참여 중인 여행이에요.</p>
              <Button variant="primary" fullWidth {loading} onclick={openTrip}>
                여행 열기
              </Button>
            </div>
          {:else if viewer.loggedIn}
            <Button variant="primary" fullWidth {loading} onclick={acceptWithAccount}>
              {loading ? "처리 중..." : "내 계정으로 입장"}
            </Button>
            {#if errorMessage}
              <p class="text-sm text-red-500 mt-3 text-center [word-break:keep-all] [overflow-wrap:break-word]">{errorMessage}</p>
            {/if}
          {:else}
            <div class="space-y-4">
              <p class="text-sm text-muted text-center [word-break:keep-all] [overflow-wrap:break-word]">
                닉네임과 이모지만 정하면<br />바로 참여할 수 있어요
              </p>

              <TextInput
                bind:value={nickname}
                placeholder="닉네임을 입력해 주세요"
                maxlength={20}
              />

              <div class="grid grid-cols-6 gap-2">
                {#each EMOJI_PRESETS as emoji}
                  <button
                    type="button"
                    onclick={() => (selectedEmoji = emoji)}
                    class="aspect-square rounded-xl flex items-center justify-center text-xl transition-colors border
                      {selectedEmoji === emoji ? 'border-teal-400 bg-teal-400/10' : 'border-white/10 bg-surface-hover'}"
                    aria-pressed={selectedEmoji === emoji}
                  >
                    {emoji}
                  </button>
                {/each}
              </div>

              {#if errorMessage}
                <p class="text-sm text-red-500 text-center [word-break:keep-all] [overflow-wrap:break-word]">{errorMessage}</p>
              {/if}

              <Button variant="primary" fullWidth {loading} onclick={joinAsGuest}>
                {loading ? "입장 중..." : "입장하기"}
              </Button>

              <p class="text-center text-sm text-muted">
                <a href={`/login?redirect=/invite/${code}`} class="text-blue-500 hover:underline">
                  계정으로 로그인하고 입장
                </a>
              </p>
            </div>
          {/if}

          <!-- Expiry Notice -->
          <p class="text-center text-xs text-muted mt-4">
            이 초대는 {formatDate(expiresAt)}까지 유효해요
          </p>
        </div>
      </div>

      {#if appStoreUrl}
        <a
          href={appStoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          class="mt-4 flex items-center justify-center gap-2 py-3 px-4 bg-surface rounded-xl text-sm text-secondary hover:bg-surface-hover transition-colors [word-break:keep-all] [overflow-wrap:break-word]"
        >
          📱 앱에서 더 편하게 — TripHelper 열기
        </a>
      {/if}
    </div>
  </div>
</Layout>
